import { Plus, X } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import useTranslations from "@/shared/lang/useTranslations";

import { INTERNAL_API_KEY_MAX_IPS } from "../constants/internalApiKeys.constants";
import type { InternalApiKeyAllowedIp } from "../types/internalApiKeys.types";
import { canonicalIp, ipProblem } from "../utils/internalApiKeyForm";

export interface InternalApiKeyIpsEditorProps {
  ips: readonly InternalApiKeyAllowedIp[];
  onChange: (ips: InternalApiKeyAllowedIp[]) => void;
  disabled?: boolean;
}

export function InternalApiKeyIpsEditor({ ips, onChange, disabled = false }: InternalApiKeyIpsEditorProps) {
  const { t } = useTranslations("internalApiKeys");
  const [newIp, setNewIp] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [ipError, setIpError] = useState<string | null>(null);

  const addIp = () => {
    const problem = ipProblem(newIp, ips);
    const ip = canonicalIp(newIp);
    if (problem || !ip) {
      setIpError(problem === "duplicate" ? t("ip_duplicate") : t("ip_invalid"));
      return;
    }
    const label = newLabel.trim();
    onChange([...ips, { ip, ...(label ? { label } : {}) }]);
    setNewIp("");
    setNewLabel("");
    setIpError(null);
  };

  return (
    <div className="space-y-3">
      {ips.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {ips.map((entry) => (
            <li key={entry.ip} className="flex items-center gap-3 px-3 py-2 text-sm">
              <code className="font-mono">{entry.ip}</code>
              {entry.label && <span className="truncate text-muted-foreground">{entry.label}</span>}
              <ColocaIconButton
                icon={X}
                variant="ghost"
                size="xs"
                className="ml-auto"
                disabled={disabled}
                aria-label={t("ip_remove", { ip: entry.ip })}
                onClick={() => onChange(ips.filter((other) => other.ip !== entry.ip))}
              />
            </li>
          ))}
        </ul>
      )}
      {ips.length < INTERNAL_API_KEY_MAX_IPS && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <ColocaInput
            value={newIp}
            onValueChange={(value) => {
              setNewIp(value);
              setIpError(null);
            }}
            placeholder={t("ip_placeholder")}
            aria-label={t("ip_label")}
            className="font-mono sm:max-w-56"
            disabled={disabled}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addIp();
              }
            }}
          />
          <ColocaInput
            value={newLabel}
            onValueChange={setNewLabel}
            placeholder={t("ip_label_placeholder")}
            aria-label={t("ip_label_placeholder")}
            maxLength={80}
            disabled={disabled}
          />
          <ColocaButton variant="default" icon={Plus} onClick={addIp} disabled={disabled || !newIp.trim()}>
            {t("ip_add")}
          </ColocaButton>
        </div>
      )}
      {ipError && <p className="text-xs text-destructive">{ipError}</p>}
    </div>
  );
}
